import { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Textarea } from "@/components/ui/TextArea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { Badge } from "@/components/ui/Badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { toast } from "@/components/ui/UseToast";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { TicketAction, TicketActionOnStatus, TicketStatus, useUpdateAssignedToMutation, useUpdateTicketStatusMutation, useUpdateTitleMutation } from "@/services/LiveQueueService";
import { calculateWaitingTime, formatTime, getStatusColor } from "@/utils/Helper";
import { Ticket } from "@/app/types/tickets";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { MultiSelect } from "@/components/ui/multiselect/MultiSelectCheckBox";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
interface TicketEditFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSave?: (ticketData: Ticket) => void;
  initialData?: Ticket;
  refetch?: () => void;
}
type FormValues = {
  title: string;
  description?: string;
  status: string;
  staff_ids?: string[];
};
// Define form interface to handle UI form state
interface TicketFormState {
  id:string;
  ticketNumber: string;
  status: string;
  itsNo: string;
  customerName: string;
  email: string;
  phoneNumber: string;
  departmentName: string;
  serviceName: string;
  title: string;
  counterNo: string;
  description: string;
  assignedTo: string[];
  servingAgent: string;
  appointmentDate: string;
  appointmentTime: string;
  createdDate: string;
  updatedDate: string;
  waitingTime: string;
  comment: string;
}

interface ValidationErrors {
  title?: string;
  description?: string;
}

export default function TicketEditForm({
  isOpen,
  setOpen,
  onSave,
  initialData,
  refetch,
}: TicketEditFormProps) {
  const [comment, setComment] = useState("");
  const defaultValues: FormValues = {
    title: "",
    description: "",
    status: "",
    staff_ids: [],
  };

  const onlySingleSpaceRegex = /^(?!.* {2,})(?!^\s)(?!.*\s$)[\s\S]*$/;

  const { data: staffData } = useGetStaffListQuery({query:""});


  const form = useForm<FormValues>({
    mode: "all",
    defaultValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;


  // Initialize with empty form state - will be populated from initialData
  const [ticketData, setTicketData] = useState<TicketFormState>({
    id:"",
    ticketNumber: '',
    status: '',
    itsNo: '',
    customerName: '',
    email: '',
    phoneNumber: '',
    departmentName: '',
    serviceName: '',
    title: '',
    counterNo: '',
    description: '',
    assignedTo: [],
    servingAgent: '',
    appointmentDate: '',
    appointmentTime: '',
    createdDate: '',
    updatedDate: '',
    waitingTime: '',
    comment: '',
  });
  const [updateTitle] = useUpdateTitleMutation();
  const [updateStatus] = useUpdateTicketStatusMutation();
  const [updateAssignedTo] = useUpdateAssignedToMutation();
  const [selectKey, setSelectKey] = useState(0);
  const [selectedAction, setSelectedAction] = useState<TicketAction | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  // const [updateTicket, { isLoading: isUpdating }] = useUpdateTicketMutation();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

  // Handle dropdown change
  const handleActionChange = (value: string) => {
    const selectedValue = value as TicketAction;
    setSelectedAction(selectedValue);
  };
  
  // Update local state when initialData changes or when the sheet opens
  useEffect(() => {
    if (initialData && isOpen) {
      setTicketData({
        id:initialData.id.toString() || "",
        ticketNumber: initialData.ticket_id || "",
        status: initialData.status || "",
        itsNo: initialData.customer?.username || "",
        customerName: initialData.customer?.name || "",
        email: initialData.customer?.email || "",
        phoneNumber: initialData.customer?.phone_number || "",
        departmentName: initialData.service?.department_name || "",
        serviceName: initialData.service?.name || "",
        title: initialData.title || "",
        counterNo: initialData.counter_id?.toString() || "",
        description: initialData.description || "",
        assignedTo:
          initialData.assigned_staff?.map((staff) => staff.id) || [],
        servingAgent: initialData.serving_agent?.name || "",
        appointmentDate: initialData.date
          ? new Date(initialData.date)
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")
          : "",
        appointmentTime: formatTime(initialData.time) || "",
        createdDate: initialData.created_at
          ? new Date(initialData.created_at)
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")
          : "",
        updatedDate: initialData.updated_at
          ? new Date(initialData.updated_at)
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")
          : "",
        waitingTime: initialData?.served_at
          ? (() => {
              const waitingMinutes = calculateWaitingTime(
                initialData?.date,
                initialData?.time,
                initialData?.served_at
              );

              if (waitingMinutes >= 60) {
                const hours = Math.floor(waitingMinutes / 60);
                const minutes = waitingMinutes % 60;
                return `${hours} hr${minutes ? ` ${minutes} min` : ""}`;
              }

              return waitingMinutes > 0 ? `${waitingMinutes} min` : "0 min";
            })()
          : "",

        comment: "",
      });

      // Fix status prefilling
      // Inside the useEffect where initialData is handled
      if (initialData.status) {
        // Convert status to uppercase with underscores
        const formattedStatus = initialData.status
          ?.toUpperCase()
          .replace(/ /g, "_") as TicketAction;

        setSelectedAction(formattedStatus);
        setSelectKey((prev) => prev + 1); // Force Select re-render
        
        setValue("status", formattedStatus); // Set the value in the form state
        setValue("title", initialData.title || ""); // Set the title value in the form state
        setValue("description", initialData.description || ""); // Set the description value in the form state
        setValue("staff_ids", initialData.assigned_staff?.map((staff) => staff.id) || []); // Set the staff_ids value in the form state
      }
    }
  }, [initialData, isOpen]);

  const handleChange = (field: string, value: string | string[]) => {
    // Replace multiple spaces with single space
    let normalizedValue = value;
    if (typeof value === 'string' && (field === "title" || field === "description")) {
      normalizedValue = value.replace(/\s{2,}/g, ' ');
    }
    
    setTicketData({ ...ticketData, [field]: normalizedValue });
    
    // Validate fields
    const newErrors = { ...validationErrors };
    
    if (field === "title") {
      if (normalizedValue.length > 50) {
        newErrors.title = "Title must not exceed 50 characters";
      } else {
        delete newErrors.title;
      }
    }
    
    if (field === "description") {
      if (normalizedValue.length > 350) {
        newErrors.description = "Description must not exceed 350 characters";
      } else {
        delete newErrors.description;
      }
    }
    
    setValidationErrors(newErrors);
  };

  const handleSave = async () => {
    // Check for validation errors before submitting
    if (Object.keys(validationErrors).length > 0) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fix the form errors before submitting",
      });
      return;
    }
    
    try {
      
      // Only if initialData exists - prevents errors with undefined values
      if (!initialData?.id) {
        throw new Error("No ticket ID found");
      }
      
      // Prepare data for API using the Ticket interface structure
      const updatedTicketData = {
        id: initialData.id,
        ticket_id: ticketData.ticketNumber,
        title: ticketData.title,
        description: ticketData.description,
        status: selectedAction || initialData.status,
        assigned_to: ticketData.assignedTo,
      };

      // Call API to update ticket
      // await updateTicket(updatedTicketData).unwrap();

      // Call the onSave function if provided
      // if (onSave && initialData) {
      //   // Map form data back to a Ticket structure for the onSave handler
      //   const updatedTicket: Ticket = {
      //     ...initialData,
      //     title: ticketData.title,
      //     description: ticketData.description,
      //     status: selectedAction || initialData.status,
      //   };
      //   await onSave(updatedTicket);
      // }

      // Success message
      toast({
        variant: "success",
        title: "Ticket updated successfully",
      });

      // Refetch data if needed
      if (refetch) {
        refetch();
      }

      // Close the sheet after saving
      handleClose();
    } catch (error) {
      console.error("Error saving ticket:", error);
      toast({
        variant: "destructive",
        title: "Failed to update ticket",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
    }
  };

 const handleClose = () => {
   setOpen(false);
   setSelectedAction(null); // Reset selection
   setSelectKey(0); // Reset key to force fresh render next time
 };


  // const onSubmit = async (data: FormValues) => {
  //   console.log("Submitted:", data);
  //   try{

  //      await updateTitle({
  //        action: TicketAction.UPDATE_DETAILS,
  //        ticketId: Number(ticketData.id),
  //        staff_id: String(userInfo?.id ?? 0),
  //        ticketData: {
  //          title: data.title,
  //          description: data.description ?? "",
  //        },
  //      }).unwrap();

  //     await updateStatus({
  //       ticket_id: Number(ticketData.id),
  //       action: data.status as TicketAction,
  //       staff_id: String(userInfo?.id ?? 0),
  //       trigger_lambda: false,
  //     }).unwrap();

  //     await updateAssignedTo({
  //       ticketId: Number(ticketData.id),
  //       assigned_to: data.staff_ids || [],
  //       action: TicketAction.ASSIGNED_TO,
  //       staff_id: String(userInfo?.id ?? 0),
  //     }).unwrap();
  //  } catch (error: any) {
  //        const message =
  //          error?.data?.detail || error?.data?.message || "Something went wrong";
  //        toast({
  //          variant: "destructive",
  //          title: "Error in Submission",
  //          description: message,
  //        });
  //      }
  //    };


  const onSubmit = async (data: FormValues) => {
    try {
      // Prepare update operations array
      const updateOperations = [
        // Update title and description
        updateTitle({
          action: TicketAction.UPDATE_DETAILS,
          ticketId: Number(ticketData.id),
          staff_id: String(userInfo?.id ?? 0),
          title: data.title,
          description: data.description ?? "",
        }).unwrap(),

        // Update assigned staff
        updateAssignedTo({
          ticketId: Number(ticketData.id),
          assigned_to: data.staff_ids || [],
          action: TicketAction.ASSIGNED_TO,
          staff_id: String(userInfo?.id ?? 0),
        }).unwrap(),
      ];

      // Define valid ticket actions
      const validActions = [
        'COMPLETE', 'HOLD', 'ABSENT', 'IN_PROGRESS', 
        'AWAITING_INFO', 'CHECKED_IN', 'ARCHIVED', 'ROUTE', 
        'COMMENT', 'UPDATE_USER', 'UPDATE_DETAILS', 'LINK', 'ASSIGNED_TO'
      ];
      // Only add status update if data.status is a valid action
      if (data.status && validActions.includes(data.status)) {
        updateOperations.push(
          updateStatus({
            ticket_id: Number(ticketData.id),
            action: data.status as TicketAction,
            staff_id: String(userInfo?.id ?? 0),
            trigger_lambda: false,
          }).unwrap()
        );
      } else if (data.status) {
        console.warn(`Invalid status value: ${data.status}. Status update skipped.`);
      }

      // Execute all operations in parallel
      await Promise.all(updateOperations);

      // Show success message
      toast({
        title: "Ticket Updated",
        description: "Ticket details have been updated successfully",
        variant: "success",
      });

      // Refetch data if needed
      if (refetch) {
        refetch();
      }

      // Close the form
      handleClose();
    } catch (error: unknown) {
      // Improved error handling with more specific types
      const typedError = error as {
        data?: { detail?: string; message?: string };
        message?: string;
        status?: number;
      };

      // Determine the most appropriate error message
      let errorMessage = "Something went wrong";

      if (typedError.data?.detail) {
        errorMessage = typedError.data.detail;
      } else if (typedError.data?.message) {
        errorMessage = typedError.data.message;
      } else if (typedError.message) {
        errorMessage = typedError.message;
      }

      // Include status code in debug logs
      if (typedError.status) {
        console.error(`Error ${typedError.status}: ${errorMessage}`);
      } else {
        console.error(`Error: ${errorMessage}`, error);
      }

      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: errorMessage,
      });
    }
  };
   const onError = () => {
      toast({
        variant: "destructive",
        description: "Please fill all the mandatory fields",
      });
    };
useEffect(() => {
  console.log("Selected Action:", selectedAction);
  console.log("Initial Status:", initialData?.status);
}, [selectedAction, initialData]);
  return (
    <Sheet open={isOpen}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar sm:w-[90%] sm:max-w-2xl"
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col py-4 gap-4"
            noValidate
          >
            <div className="flex flex-col gap-4 bg-gray-100 h-screen">
              <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
                <SheetTitle>
                  <div className="flex justify-between gap-5 items-center bg-white">
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl font-semibold">Edit Ticket</h1>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={handleClose}
                        className="w-[100px] gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                      >
                        <i className="fa-regular fa-x text-xs font-semibold"></i>
                        Cancel
                      </Button>
                      <LoadingButton
                        type="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {!isSubmitting && (
                          <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                        )}
                        Save
                      </LoadingButton>
                    </div>
                  </div>
                </SheetTitle>
              </SheetHeader>

              <SheetDescription className="p-4 mx-4 bg-white rounded-lg">
                {/* Ticket Title */}
                <div className="p-4 border-b flex items-center justify-between">
                  <h2 className="text-lg font-semibold">
                    <Tooltip>
                      <TooltipTrigger>
                        <p className="capitalize text-nowrap max-w-[500px] truncate overflow-hidden">
                          {ticketData.title}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent>{ticketData.title}</TooltipContent>
                    </Tooltip>
                  </h2>
                  <Badge
                    className={`${ticketData.status ? getStatusColor(ticketData.status as TicketStatus) : ""} text-white`}
                  >
                    {ticketData.status.replace(/_/g, " ")}
                  </Badge>
                </div>

                {/* Form */}
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Row 1 */}
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Ticket Number
                    </label>
                    <Input value={ticketData.ticketNumber} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Service Name
                    </label>
                    <Input value={ticketData.serviceName} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Department Name
                    </label>
                    <Input value={ticketData.departmentName} disabled />
                  </div>
                  <div className="space-y-2">
                    {/* <label className="text-sm text-primary">Status</label>
                <Select
                  key={selectKey}
                  value={selectedAction || ""}
                  onValueChange={handleActionChange}
                >
                  <SelectTrigger className="capitalize border-primary w-[90%] flex justify-between items-center px-4 py-2 text-center border bg-background rounded-md shadow-sm">
                    <SelectValue
                      placeholder="Select status"
                      className="text-gray-400 mx-auto"
                    >
                      {selectedAction?.replace(/_/g, " ").toLowerCase()}
                    </SelectValue>
                  </SelectTrigger>

                  <SelectContent className="flex justify-start items-center">
                    {Object.values(TicketActionOnStatus).map(
                      (action: string) => (
                        <SelectItem
                          key={action}
                          value={action}
                          className="text-center flex justify-start"
                        >
                          {action === TicketAction.COMPLETE
                            ? "Complete"
                            : action.replace(/_/g, " ").toLowerCase()}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select> */}
                    <label className="text-sm text-primary">Status *</label>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: "Status is required" }}
                      render={({ field }) => (
                        <Select
                          key={selectKey}
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className="capitalize border-primary w-[90%] flex justify-between items-center px-4 py-2 border bg-background rounded-md shadow-sm">
                            <SelectValue>
                              {field.value
                                ? field.value === TicketAction.COMPLETE
                                  ? "Complete"
                                  : field.value.replace(/_/g, " ").toLowerCase()
                                : "Select status"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(TicketActionOnStatus).map(
                              (action: string) => (
                                <SelectItem
                                  key={action}
                                  value={action}
                                  className="text-start capitalize"
                                >
                                  {action === TicketAction.COMPLETE
                                    ? "Complete"
                                    : action.replace(/_/g, " ").toLowerCase()}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.status && (
                      <p className="text-red-500 text-xs">
                        {errors.status.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">ITS No</label>
                    <Input value={ticketData.itsNo} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Customer Name
                    </label>
                    <Input value={ticketData.customerName} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">Email</label>
                    <Input value={ticketData.email} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Phone Number
                    </label>
                    <Input value={ticketData.phoneNumber} disabled />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <label className="text-sm text-primary">Title</label>
                    <div className="space-y-1">
                      {/* <Input
                    value={ticketData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    className={`w-full border ${validationErrors.title ? "border-red-500" : "border-primary"}`}
                    maxLength={50}
                  />
                  {validationErrors.title && (
                    <p className="text-xs text-red-500">
                      {validationErrors.title}
                    </p>
                  )} */}
                      <Input
                        type="text"
                        {...register("title", {
                          required: "Title is required",
                          maxLength: {
                            value: 50,
                            message: "Title must be under 50 characters",
                          },
                          pattern: {
                            value: onlySingleSpaceRegex,
                            message: "Only single spaces allowed between words",
                          },
                        })}
                        onChange={(e) => handleChange("title", e.target.value)}
                        className={`w-full border ${validationErrors.title ? "border-red-500" : "border-primary"}`}
                        maxLength={50}
                        placeholder="Enter title"
                      />
                      {errors.title && (
                        <p className="text-red-500 text-xs">
                          {errors.title.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {ticketData.title.length}/50 characters
                      </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-10">
                      <div className="space-y-2">
                        <label className="text-sm text-gray-500">
                          Counter No
                        </label>
                        <Input value={ticketData.counterNo} disabled />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-primary">
                          Assign To
                        </label>
                        <Controller
                          name="staff_ids"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <MultiSelect
                              // floatingLabel="Assign staff"
                              text="*"
                              options={
                                staffData
                                  ? staffData.map((staff) => ({
                                      label: staff.label,
                                      value: staff.value,
                                    }))
                                  : []
                              }
                              defaultValue={watch("staff_ids") || []}
                              onValueChange={(selectedValues) =>
                                onChange(selectedValues)
                              }
                              placeholder="Select Staff"
                              variant="inverted"
                              maxCount={1}
                              className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 min-w-full max-w-[200px] w-[200px]"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <label className="text-sm text-primary">Description:</label>
                    <div className="space-y-1">
                      <Textarea
                        {...register("description", {
                          maxLength: {
                            value: 350,
                            message: "Description must be under 350 characters",
                          },
                          pattern: {
                            value: onlySingleSpaceRegex,
                            message: "Only single spaces allowed between words",
                          },
                        })}
                        maxLength={350}
                        onChange={(e) =>
                          handleChange("description", e.target.value)
                        }
                        placeholder="Optional description"
                        className="w-full border rounded-md px-4 py-2 min-h-[120px] text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {errors.description && (
                        <p className="text-red-500 text-xs">
                          {errors.description.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {ticketData.description.length}/350 characters
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Serving Agent
                    </label>
                    <Input value={ticketData.servingAgent} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Appointment Date
                    </label>
                    <Input value={ticketData.appointmentDate} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">
                      Appointment Time
                    </label>
                    <Input value={ticketData.appointmentTime} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">Created At</label>
                    <Input value={ticketData.createdDate} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">Updated At</label>
                    <Input value={ticketData.updatedDate} disabled />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-500">Wait Time</label>
                    <Input value={ticketData.waitingTime} disabled />
                  </div>
                </div>
              </SheetDescription>
            </div>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
}
